/* lp-flip — the reveal queue behind the flip-to-unlock landing page.

   WHAT IS ACTUALLY RANDOM. The SET is fixed: every visitor reveals every
   gift flagged in_reveal, exactly once. Only the ORDER is shuffled, and
   only the position clicked is the visitor's own. That is the whole trick,
   and it is why the PDP can honestly say "your gifts, in the order you
   picked them" — the order is real even though the outcome never was.

   NOBODY LOSES, WITHIN THE SET THIS FUNCTION IS GIVEN. The queue this
   builds is fixed at revealCount, not at the in_reveal count: every gift
   that makes it into the queue is dealt exactly once and none is weighted
   — `shuffle` reorders, it never drops or duplicates. `.slice(0,
   Math.min(asked, pool.length))` IS a genuine clamp, on purpose and
   covered by scripts/flip-reveal.test.mjs (`buildQueue(GIFTS, 3, …).length
   === 3` against a pool of 5) — this function has no way to tell "asked
   for fewer on purpose" apart from "asked for fewer by mistake" and does
   not guess. A card is a position, not a container: it holds nothing until
   clicked, and what it then shows comes off the head of this queue. A
   revealCount lower than the number of gifts flagged in_reveal is a
   misconfiguration, not something buildQueue silently corrects — Task 4's
   section setting ("Gifts to reveal") is what must clamp itself to the
   in_reveal count before this is ever called.

   THE QUEUE IS PURE. buildQueue takes gifts, a count and a generator, and
   returns an array. It touches no DOM and reads no globals, which is what
   lets scripts/flip-reveal.test.mjs interrogate every degenerate case in a
   millisecond. The DOM controller lives below the pure layer and is added
   in Task 4. */
(() => {
  'use strict';

  /* Fisher-Yates. Copies first: the caller's gift list comes from the
     rendered offer and is read again by the strip, so mutating it would
     reorder the page's own source of truth. */
  const shuffle = (items, rng) => {
    const out = Array.isArray(items) ? items.slice() : [];
    const random = typeof rng === 'function' ? rng : Math.random;
    for (let i = out.length - 1; i > 0; i -= 1) {
      const j = Math.floor(random() * (i + 1));
      const swap = out[i];
      out[i] = out[j];
      out[j] = swap;
    }
    return out;
  };

  /* A gift with no slug is unusable rather than defaulted: the slug is what
     travels in cn_gifts, so inventing one would put a value in a shopper's
     URL that no surface can resolve. */
  const usable = (gift) => Boolean(gift && gift.in_reveal && gift.slug);

  const buildQueue = (gifts, revealCount, rng) => {
    const pool = (Array.isArray(gifts) ? gifts : []).filter(usable);
    if (pool.length === 0) return [];
    const asked = Math.trunc(Number(revealCount));
    if (!Number.isFinite(asked) || asked <= 0) return [];
    return shuffle(pool, rng).slice(0, Math.min(asked, pool.length));
  };

  const slugsOf = (queue) =>
    (Array.isArray(queue) ? queue : []).map((g) => g && g.slug).filter(Boolean);

  window.lpFlip = { shuffle, buildQueue, slugsOf };

  /* ------------------------------------------------------------ controller

     Everything below paints. The decisions were all made above, which is
     why nothing here recomputes what a card should show — it takes the next
     gift off the queue and renders it.

     MOTION IS OPTIONAL, THE GIFT IS NOT. The idle nudge and the confetti
     both check prefers-reduced-motion and simply do not run when it is set.
     For this audience that is correctness, not polish: nothing about which
     gifts you get, or whether you can finish the flow, depends on either. */

  const reduced = () => {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  };

  const parseOffer = (root) => {
    try {
      return JSON.parse(root.getAttribute('data-offer') || '{}');
    } catch (e) {
      /* An unreadable offer leaves the section inert rather than half-alive.
         The server-rendered board stays on screen and nothing flips. */
      return null;
    }
  };

  const track = (step) => {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'lp_step_view', step: step });
    } catch (e) {
      /* Analytics never breaks the game. */
    }
  };

  const init = (root) => {
    const offer = parseOffer(root);
    if (!offer) return;

    const cards = Array.from(root.querySelectorAll('[data-lp-flip-card]'));
    const button = root.querySelector('[data-lp-flip-all]');
    const counter = root.querySelector('[data-lp-flip-counter]');
    const canvas = root.querySelector('[data-lp-flip-confetti]');
    if (cards.length === 0) return;

    const wanted = Math.trunc(Number(root.getAttribute('data-reveal-count'))) || 5;
    const queue = buildQueue(offer.gifts, Math.min(wanted, cards.length), Math.random);
    if (queue.length === 0) return;

    const total = queue.length;
    const revealed = [];
    let started = false;

    const setCounter = () => {
      if (!counter) return;
      counter.hidden = false;
      counter.textContent = 'You unlocked ' + revealed.length + ' / ' + total;
    };

    /* Completing the board and advancing to step 2 are deliberately two
       moments. The reveal is the reward — swapping the view out from under
       it the instant the last card turns would spend that reward on nothing.
       So the board completes, the confetti runs, the button becomes the way
       onward, and the visitor leaves when they are ready. */
    const finish = () => {
      root.setAttribute('data-game-status', 'complete');
      if (button) button.textContent = root.getAttribute('data-complete-label') || 'Continue';
      confetti();
    };

    /* THE STEP SWAP. This is a lander, not a page: step 2 replaces step 1
       rather than sitting below it, so the question lands in front of the
       visitor with nothing to scroll to and nothing above it to re-read. */
    const advance = () => {
      root.hidden = true;
      document.dispatchEvent(
        new CustomEvent('lp:flip-complete', {
          detail: { slugs: slugsOf(revealed), gifts: revealed.slice() },
        })
      );
      window.scrollTo(0, 0);
    };

    const flip = (card) => {
      if (card.classList.contains('is-flipped')) return;
      if (revealed.length >= total) return;

      if (!started) {
        started = true;
        root.setAttribute('data-game-status', 'in-progress');
        track('flip');
      }

      const gift = queue[revealed.length];
      const face = card.querySelector('[data-lp-flip-face]');
      const back = card.querySelector('.lp-flip__back');

      if (face) {
        face.textContent = '';
        if (gift.image) {
          const img = document.createElement('img');
          img.className = 'lp-flip__face-img';
          img.src = gift.image;
          img.alt = '';
          img.loading = 'lazy';
          face.appendChild(img);
        }
        const label = document.createElement('span');
        label.textContent = gift.label || '';
        face.appendChild(label);
        face.hidden = false;
      }
      if (back) back.hidden = true;

      card.classList.add('is-flipped');
      card.classList.remove('is-nudging');
      card.setAttribute('aria-label', 'Unlocked: ' + (gift.label || ''));
      revealed.push(gift);
      setCounter();

      if (revealed.length === total) finish();
    };

    /* The button flips whatever is left on a stagger, for anyone who does
       not want to tap five times. Once complete it becomes the way onward. */
    const flipRest = () => {
      if (root.getAttribute('data-game-status') === 'complete') {
        advance();
        return;
      }
      const left = cards.filter((c) => !c.classList.contains('is-flipped'));
      const need = total - revealed.length;
      left.slice(0, need).forEach((card, i) => {
        window.setTimeout(() => flip(card), reduced() ? 0 : i * 180);
      });
    };

    cards.forEach((card) => card.addEventListener('click', () => flip(card)));
    if (button) button.addEventListener('click', flipRest);

    /* Idle nudge: one untouched card twitches every so often so the board
       reads as alive rather than as a static image. */
    const nudge = () => {
      if (reduced()) return;
      if (root.getAttribute('data-game-status') !== 'initial') return;
      const idle = cards.filter(
        (c) => !c.classList.contains('is-flipped') && !c.classList.contains('is-nudging')
      );
      if (idle.length === 0) return;
      const card = idle[Math.floor(Math.random() * idle.length)];
      card.classList.add('is-nudging');
      window.setTimeout(() => card.classList.remove('is-nudging'), 800);
      window.setTimeout(nudge, 1800);
    };
    window.setTimeout(nudge, 2000);

    /* Confetti in the brand's own colours, read off the page rather than
       hardcoded, so it cannot drift from the palette. */
    const confetti = () => {
      if (reduced() || !canvas || !canvas.getContext) return;
      const ctx = canvas.getContext('2d');
      const styles = getComputedStyle(document.documentElement);
      const palette = ['--lp-spanish', '--lp-sea', '--lp-olive', '--lp-puce', '--lp-current']
        .map((t) => styles.getPropertyValue(t).trim())
        .filter(Boolean);
      if (palette.length === 0) return;

      canvas.width = root.offsetWidth;
      canvas.height = root.offsetHeight;

      const bits = Array.from({ length: 90 }, () => ({
        x: Math.random() * canvas.width,
        y: -20 - Math.random() * canvas.height * 0.5,
        r: Math.random() * 5 + 3,
        d: Math.random() * 3 + 1.5,
        c: palette[Math.floor(Math.random() * palette.length)],
        tilt: Math.random() * 10 - 5,
      }));

      const t0 = Date.now();
      const draw = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        let live = false;
        for (const p of bits) {
          if (p.y < canvas.height) live = true;
          ctx.beginPath();
          ctx.fillStyle = p.c;
          ctx.ellipse(p.x, p.y, p.r, p.r / 2, p.tilt, 0, Math.PI * 2);
          ctx.fill();
          p.y += p.d;
          p.tilt += 0.08;
        }
        if (live && Date.now() - t0 < 6000) {
          window.requestAnimationFrame(draw);
        } else {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      };
      window.requestAnimationFrame(draw);
    };
  };

  const boot = () => {
    document.querySelectorAll('[data-lp-flip]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
